// 주간 이벤트 표시 모델(3개월 C5): 일일 장부(weekly) → 줄 목록. DOM 없이 테스트한다.
import { liveGoalReady, type DailyState } from '../shared/daily';
import { regionGoldMult } from '../shared/content/economy';
import { liveDaysLeft, liveGoalKey, liveSeasonOf, liveWeekOf, type LiveGoalType, type LiveKind } from '../shared/content/liveops';
import type { RegionNo } from '../shared/content/types';

export interface LiveGoalView {
  key: string; name: string; type: LiveGoalType; progress: number; count: number; claimed: boolean; claimable: boolean;
  /** 지역 배율을 곱한 실제 골드 */ gold: number; stones: number; text: string;
}
export interface LiveView {
  active: boolean;
  name: string; kind: LiveKind | ''; blurb: string; season: number; daysLeft: number;
  /** 주차 그림(assetUrl 전 경로, 3개월 C8 Codex image_gen 회차): 머리 띠 배경 배너·엠블럼. 비활성이면 빈 문자열 */
  banner: string; emblem: string;
  tint: number; rim: number; bgm: string;
  goals: LiveGoalView[];
  finale: { gold: number; stones: number; claimable: boolean; claimed: boolean };
  /** 이벤트 모드 한 줄들(추가 입장·보상 배율) */
  mods: string[];
  /** 머리줄 알약 문구와 받을 보상 점 */
  chip: string; dot: boolean;
}
const INACTIVE: LiveView = { active: false, name: '', kind: '', blurb: '', banner: '', emblem: '', season: 0, daysLeft: 0, tint: 0, rim: 0, bgm: '', goals: [], finale: { gold: 0, stones: 0, claimable: false, claimed: false }, mods: [], chip: '', dot: false };

/** 주차 id → 배너(ui/live/banner-<id>.webp, 900×300)·엠블럼(ui/icons/emblem-live-<id>.webp, 128×128) 경로 */
export function liveArt(id: string): { banner: string; emblem: string } {
  return { banner: 'ui/live/banner-' + id + '.webp', emblem: 'ui/icons/emblem-live-' + id + '.webp' };
}

export function liveView(s: DailyState, town: number): LiveView {
  const w = liveWeekOf(s.day);
  if (!w || s.weekly.week < 0) return INACTIVE;
  const mult = regionGoldMult(Math.min(6, Math.max(1, Math.floor(town) || 1)) as RegionNo);
  const goals: LiveGoalView[] = w.goals.map((g) => {
    const key = liveGoalKey(w, g), row = s.weekly.goals[key];
    const ready = liveGoalReady(s, g, row);
    const claimed = !!row && row.claimed;
    const progress = claimed || ready ? g.count : row ? row.progress : 0;
    return { key, name: g.name, type: g.type, progress, count: g.count, claimed, claimable: !claimed && ready, gold: Math.round(g.gold * mult), stones: g.stones, text: claimed ? '수령 완료' : progress + ' / ' + g.count };
  });
  const allClaimed = goals.every((x) => x.claimed);
  const mods: string[] = [];
  const m = w.mods;
  if (m.vaultExtra) mods.push('황금 보물고 하루 +' + m.vaultExtra + '번');
  if (m.mineExtra) mods.push('강화석 광산 하루 +' + m.mineExtra + '번');
  if (m.vaultGold !== 1) mods.push('보물고 골드 ×' + m.vaultGold);
  if (m.mineStones !== 1) mods.push('광산 강화석 ×' + m.mineStones);
  if (m.towerGold !== 1) mods.push('탑 첫 클리어 골드 ×' + m.towerGold);
  const finale = { gold: Math.round(w.finale.gold * mult), stones: w.finale.stones, claimed: s.weekly.finale, claimable: allClaimed && !s.weekly.finale };
  const daysLeft = liveDaysLeft(s.day);
  return {
    active: true, name: w.name, kind: w.kind, blurb: w.blurb, ...liveArt(w.id), season: liveSeasonOf(s.day), daysLeft, tint: w.tint, rim: w.rim, bgm: w.bgm,
    goals, finale, mods, chip: '이벤트 · ' + daysLeft + '일', dot: goals.some((x) => x.claimable) || finale.claimable,
  };
}
